import { useTranslation } from 'react-i18next';
import { useAuthState } from '../../hooks/useAuthState';
import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode } from '@fluentui/react';
import { getAllOrganisations } from '../../firebase';
import { Organisation } from '../../interfaces';
import { Container } from 'react-bootstrap';

const OrganisationsList = () => {
  const { userRoles } = useAuthState();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [organisations, setOrganisations] = useState<Organisation[]>(userRoles.isSuperAdmin ? [] : userRoles.adminIn);
  const organisationsRef = useRef<Organisation[]>([]);
  useEffect(() => {
    organisationsRef.current = organisations;
  }, [organisations]);

  const _onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    const newItems = _copyAndSort(currColumn.fieldName!, currColumn.isSortedDescending);
    console.log('After calling _copyAndSort => newItems:', newItems);
    setColumns(newColumns);
    setOrganisations(newItems);
  };

  const _copyAndSort = (columnKey: string, isSortedDescending?: boolean): Organisation[] => {
    const key = columnKey as keyof Organisation;
    return organisationsRef.current
      .slice(0)
      .sort((a: Organisation, b: Organisation) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
  };
  const [columns, setColumns] = useState<IColumn[]>([
    {
      key: 'name',
      name: 'Namn',
      fieldName: 'name',
      data: 'string',
      minWidth: 210,
      maxWidth: 350,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: 'Sorted A to Z',
      sortDescendingAriaLabel: 'Sorted Z to A',
      isPadded: true,
      onColumnClick: _onColumnClick
    },
    {
      key: 'createdAt',
      name: 'Skapades',
      fieldName: 'createdAt',
      data: 'TimeStamp',
      minWidth: 210,
      maxWidth: 350,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: 'Sorted A to Z',
      sortDescendingAriaLabel: 'Sorted Z to A',
      isPadded: true,
      onColumnClick: _onColumnClick,
      onRender: (item: Organisation) => <span>{item.createdAt.toDate().toLocaleDateString()}</span>
    },
    {
      key: 'arrow',
      name: '',
      minWidth: 30,
      onRender: () => <i className='fa-solid fa-chevron-right' style={{ fontSize: 20 }}></i>
    }
  ]);

  useEffect(() => {
    if (!userRoles || (!userRoles.isSuperAdmin && userRoles.adminIn.length < 1)) navigate('/');
  }, [navigate, userRoles, location]);

  useEffect(() => {
    if (!userRoles.isSuperAdmin) return;

    (async () => {
      const _organisations = await getAllOrganisations();
      setOrganisations(_organisations);
    })();
  }, [userRoles.isSuperAdmin]);

  const onRenderRow = useCallback(
    (row, defaultRender) => {
      return cloneElement(defaultRender(row), { onClick: () => navigate(row.item.id) });
    },
    [navigate]
  );

  return (
    <Container>
      <div>
        {userRoles.isSuperAdmin && (
          <button
            className='btn btn-primary fab'
            data-toggle='tooltip'
            data-placement='top'
            title={t('organisations.createNew')}
            onClick={() => navigate('create')}
          >
            {t('organisations.createNew')}
          </button>
        )}

        <h1 className='h1 mb-3'>{t('organisations.pageTitle')}</h1>
        <h4>{t('organisations.adminOrgListTitle')}</h4>
        <div style={{ width: '100%', height: '70vh', overflow: 'scroll', marginTop: '2rem' }}>
          <DetailsList
            items={organisations}
            compact={false}
            columns={columns}
            selectionMode={SelectionMode.none}
            getKey={(item) => item.id}
            setKey='none'
            layoutMode={DetailsListLayoutMode.justified}
            isHeaderVisible={true}
            onRenderRow={onRenderRow}
          />
        </div>
      </div>
    </Container>
  );
};

export default OrganisationsList;
